"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useScreenShareStartedAction = void 0;
var _regenerator = _interopRequireDefault(require("@babel/runtime/regenerator"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _react = require("react");
var _useCommonPageActionsApi = require("../../client/react/useCommonPageActionsApi");
var _logger = require("@studio/logger");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Custom hook to wrap screen share operations with analytics tracking.
 * Handles measurement start/end with proper observer cleanup using temporary observer pattern.
 *
 * @param {Object} meetingSession - The Chime meeting session object
 * @returns {Function} startScreenShareWithTracking - Function to start screen share with analytics
 */
var useScreenShareStartedAction = exports.useScreenShareStartedAction = function useScreenShareStartedAction(meetingSession) {
  var _useCommonPageActions = (0, _useCommonPageActionsApi.useCommonPageActionsApi)(),
    startScreenShareStarted = _useCommonPageActions.startScreenShareStarted,
    endScreenShareStarted = _useCommonPageActions.endScreenShareStarted;
  var tempObserverRef = (0, _react.useRef)(null);

  /**
   * Starts screen share with analytics tracking.
   *
   * Usage:
   *   await startScreenShareWithTracking(
   *     { screenShareType: 'WINDOW', tabAudioShared: false },
   *     async () => {
   *       await meeting.meetingSession.audioVideo.startContentShare(stream);
   *     }
   *   );
   *
   * @param {Object} analyticsProps - Analytics properties
   * @param {string} analyticsProps.screenShareType - Type of screen share (WINDOW, TAB, etc)
   * @param {boolean} analyticsProps.tabAudioShared - Whether tab audio is shared
   * @param {Function} operationFn - Async function that performs the screen share operation
   * @returns {Promise} Result of the operation function
   */
  var startScreenShareWithTracking = (0, _react.useCallback)( /*#__PURE__*/function () {
    var _ref = (0, _asyncToGenerator2["default"])( /*#__PURE__*/_regenerator["default"].mark(function _callee(analyticsProps, operationFn) {
      var cleanupTimeout, observerObj, result;
      return _regenerator["default"].wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (meetingSession !== null && meetingSession !== void 0 && meetingSession.audioVideo) {
              _context.next = 2;
              break;
            }
            throw new Error('Meeting session audioVideo not available');
          case 2:
            // Clean up any existing observer first
            if (tempObserverRef.current) {
              meetingSession.audioVideo.removeContentShareObserver(tempObserverRef.current);
              tempObserverRef.current = null;
            }

            // Create temporary observer that captures props in closure
            observerObj = {
              contentShareDidStart: function contentShareDidStart() {
                // Clear the safety timeout since the event fired
                if (cleanupTimeout) {
                  clearTimeout(cleanupTimeout);
                }
                endScreenShareStarted(analyticsProps);
                meetingSession.audioVideo.removeContentShareObserver(tempObserverRef.current);
                tempObserverRef.current = null;
              }
            };
            tempObserverRef.current = observerObj;

            // Safety timeout: if contentShareDidStart doesn't fire within 30 seconds, clean up
            cleanupTimeout = setTimeout(function () {
              if (tempObserverRef.current === observerObj) {
                meetingSession.audioVideo.removeContentShareObserver(tempObserverRef.current);
                tempObserverRef.current = null;

                // Call end with error
                endScreenShareStarted(_objectSpread(_objectSpread({}, analyticsProps), {}, {
                  error: 'Screen share start event did not fire within 30 seconds'
                }));
              }
            }, 30000);

            // Register the temporary observer
            meetingSession.audioVideo.addContentShareObserver(tempObserverRef.current);

            // Start measurement
            startScreenShareStarted(analyticsProps);
            _context.prev = 8;
            _context.next = 11;
            return operationFn();
          case 11:
            result = _context.sent;
            return _context.abrupt("return", result);
          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](8);
            // Clean up observer and timeout on error
            if (cleanupTimeout) {
              clearTimeout(cleanupTimeout);
            }
            if (tempObserverRef.current) {
              meetingSession.audioVideo.removeContentShareObserver(tempObserverRef.current);
              tempObserverRef.current = null;
            }

            // Call end with error
            endScreenShareStarted(_objectSpread(_objectSpread({}, analyticsProps), {}, {
              error: (0, _logger.errorReplacer)(_context.t0)
            }));

            // Re-throw so caller can handle
            throw _context.t0;
          case 21:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[8, 15]]);
    }));
    return function (_x, _x2) {
      return _ref.apply(this, arguments);
    };
  }(), [meetingSession, startScreenShareStarted, endScreenShareStarted]);
  return {
    startScreenShareWithTracking: startScreenShareWithTracking
  };
};